import {
  faExclamationTriangle,
  faFile,
  faSave,
  faUpload,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Input } from "@ventumsoftware/react-toolkit";
import moment from "moment";
import type React from "react";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useApiCall, ValidationError } from "../api/api";
import { PageHeader } from "../components/content/PageHeader";
import { Button } from "../components/form/Button";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";

export const AddDefaultInspectionAttachmentModal: FC<{
  reload: (silently: boolean) => Promise<void>;
}> = (props) => {
  const { uploadDefaultInspectionAttachment } = useApiCall();

  const [name, setName] = useState<string>("");
  const [order, setOrder] = useState<number>(1);
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(new Date());
  const [file, setFile] = useState<File | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (name === "") {
        setName(selectedFile.name);
      }
    }
  };

  const previewUrl = useMemo(() => {
    if (!file || !file.type.startsWith("image/")) {
      return null;
    }

    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  function getExtension(filename: string | undefined) {
    if (filename === undefined) {
      return;
    }
    return filename.split(".").pop()!.toLowerCase();
  }

  function hasMatchingExtension() {
    if (!file || !name) return false;

    const inputExtension = getExtension(name);
    const fileExtension = getExtension(file.name);

    return inputExtension === fileExtension;
  }

  const save = async () => {
    if (!file) {
      return;
    }

    await uploadDefaultInspectionAttachment(file, name, order, fromDate, toDate)
      .catch((e) => {
        if (e instanceof ValidationError) {
          setErrors(Object.values(e.errors).flat());
        }
        throw e;
      })
      .then(() => {
        props.reload(true);
      });
  };

  return (
    <>
      <div className={"flex flex-col gap-y-4"}>
        <div className={"grid grid-cols-4 gap-4"}>
          <div className={"col-span-3"}>
            <Input type={"text"} label={"Naam"} value={name} onChange={setName} />
          </div>
          <div className={"col-span-1"}>
            <Input
              type={"number"}
              label={"Volgorde"}
              value={order}
              onChange={setOrder}
              min={1}
              step={1}
            />
          </div>
        </div>

        <div className={"grid grid-cols-2 gap-4"}>
          <div>
            <Input type={"date"} label={"Vanaf"} value={fromDate} onChange={setFromDate} />
          </div>
          <div>
            <Input type={"date"} label={"Tot en met"} value={toDate} onChange={setToDate} />
          </div>
        </div>

        {!hasMatchingExtension() && file && name && (
          <div
            className={
              "flex flex-row gap-2 px-2 py-3 items-center bg-red-50 border border-red-400 rounded-lg"
            }
          >
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div>
              <strong>Let op: </strong> De bestandsextensie van de opgegeven naam komt niet overeen
              met het geüploade bestand.
            </div>
          </div>
        )}

        {file ? (
          <>
            <div className={"mt-3 flex items-center gap-3 rounded-md border bg-white p-2"}>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={file.name}
                  className={"h-12 w-12 rounded object-cover"}
                />
              ) : (
                <div
                  className={
                    "flex h-12 w-12 items-center justify-center rounded bg-gray-100 text-gray-500"
                  }
                >
                  <FontAwesomeIcon icon={faFile} />
                </div>
              )}
              <div className={"min-w-0 flex-1"}>
                <p className={"truncate text-sm font-medium"}>{file.name}</p>
                <p className={"text-xs text-gray-500"}>{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <IconButton
                type={"danger"}
                icon={faX}
                size={"md"}
                onClick={() => {
                  setFile(undefined);
                  setName("");
                }}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <div
                className={
                  "w-full relative border border-brand-900 rounded-lg border-dashed group hover:bg-brand-50"
                }
              >
                <input
                  type={"file"}
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className={"opacity-0 absolute inset-0 z-20"}
                  accept={".pdf"}
                />
                <div className={"flex flex-col items-center justify-center gap-y-2 py-4"}>
                  <div className={"text-sm text-brand-900 text-center"}>
                    Klik hier om een bestand toe te voegen.
                  </div>
                  <FontAwesomeIcon icon={faUpload} className={"text-xl text-brand-900"} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <ModalFooter text={"Uploaden"} icon={faUpload} onSubmit={save} />
    </>
  );
};
